import { Injectable, signal, computed, inject } from '@angular/core';
import {
  CockpitLayout,
  CockpitRow,
  defaultVideoProperties,
  GridComponent,
  ILayout,
  Irow,
  videoProperties,
  GridItem,
  IComponents,
  IColumns,
  firstLayout,
  IRows,
  IComponent,
} from './cockpitDesigner.models';
import { ComponentKey } from '../cockpitComponents';
import { defaultRowElementProperties } from './designer.constants';
import { ElementProperties } from './designerCSSPropertiesForm/designerCSSPropertiesForm';
import { catchError, firstValueFrom, map, of, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class CockpitDesignerStore {
  private readonly _layout = signal<CockpitLayout>({
    videoConfig: defaultVideoProperties,
    rows: [],
  });

  private readonly _newLayout = signal<ILayout>({
    id: crypto.randomUUID(),
    rowsLength: 0,
    rows: {},
    videoConfig: defaultVideoProperties,
  });

  readonly assignedDeviceIDs = signal<string[]>([]);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  readonly layout = this._layout.asReadonly();
  private http = inject(HttpClient);
  id = toSignal(this.route.queryParamMap.pipe(map((params) => params.get('id'))));

  readonly newLayout = this._layout.asReadonly();

  readonly rows = computed(() => this._layout().rows);
  readonly videoConfig = computed(() => this._layout().videoConfig);

  readonly selectedRowId = signal<string | null>(null);
  readonly touched = signal<boolean>(false);

  async fetchLayoutById(id: string): Promise<ILayout | null> {
    console.log('fetching');
    try {
      const response = await firstValueFrom(
        this.http.get<{
          layout: {
            data: { dataJSON: ILayout };
          };
          assignedDevices: string[];
        }>(`/api/devices/layout/${id}`),
      );
      this.assignedDeviceIDs.set(response.assignedDevices);
      console.log('new assigned device Ids', this.assignedDeviceIDs());
      return response.layout.data.dataJSON as ILayout;
    } catch (error) {
      return null;
    }
  }

  setDeviceId(id: string) {
    return this.router.navigate([], {
      queryParams: { id },
      queryParamsHandling: 'merge',
    });
  }

  async load(layout: CockpitLayout) {
    const id = this.id();
    if (id) {
      console.log('found the id');
      const foundLayout = await this.fetchLayoutById(id);
      if (foundLayout) {
        const transformedLayout = this.transformLayout(foundLayout);
        this._newLayout.set(foundLayout);
        this._layout.set(transformedLayout);
        await this.setDeviceId(this._newLayout().id);
        return;
      }
      const transformedLayout = this.transformLayout(firstLayout);

      this.loadNewLayout(firstLayout);

      this._layout.set(transformedLayout);
      await this.setDeviceId(this._newLayout().id);
    }

    const transformedLayout = this.transformLayout(firstLayout);

    this.loadNewLayout(firstLayout);

    this._layout.set(transformedLayout);
    await this.setDeviceId(this._newLayout().id);
  }

  loadNewLayout(newLayout: ILayout) {
    this._newLayout.set(newLayout);
  }

  updateCockpit(event: {gridComponent: GridComponent, rowId: string, columnId:string}){
    console.log('cockpit service event', event)
    this._newLayout.update((layout: ILayout)=>{
      const newLayout: ILayout = {
        ...layout,
      }
      newLayout.rows[event.rowId].columns[event.columnId].components[event.gridComponent.id] = event.gridComponent;
      return newLayout
    })

    const transformedLayout = this.transformLayout(this._newLayout());
    this._layout.update(() => transformedLayout);
    this.touched.set(true);
  }

  addNewRow() {
    const currentRowsLength = this._newLayout().rowsLength;
    const colId1 = crypto.randomUUID();
    const colId2 = crypto.randomUUID();
    const rowId = crypto.randomUUID();
    const newRow: Irow = {
      columnsLength: 2,
      columns: {
        [`${colId1}`]: {
          id: colId1,
          position: 0,
          components: {},
          componentsLength: 0,
          parentRowId: rowId,
        },
        [`${colId2}`]: {
          id: colId2,
          position: 1,
          components: {},
          componentsLength: 0,
          parentRowId: rowId,
        },
      },
      position: currentRowsLength + 1,
      id: rowId,
      height: 200,
      properties: defaultRowElementProperties,
    };
    this._newLayout.update((layout) => {
      const newLayout: ILayout = {
        ...layout,
        rowsLength: layout.rowsLength + 1,
        rows: {
          ...layout.rows,
          [`${newRow.id}`]: newRow,
        },
      };
      console.log('this is the new layout before saving', newLayout);
      return newLayout;
    });

    const transformedLAyout = this.transformLayout(this._newLayout());
    this._layout.update(() => transformedLAyout);
    this.touched.set(true);
  }

  addComponnet(component: ComponentKey, rowId: string, colId: string, position: number) {
    console.log('component', component);
    const newComponentId = crypto.randomUUID();
    const layout = this._newLayout();
    const newComponent: GridComponent = {
      id: newComponentId,
      position,
      name: component,
      componentSettings:{
                componentId:'string',
                componentName: 'string1'
              }
    };
    layout.rows[rowId].columns[colId].components[newComponentId] = newComponent;
    this._newLayout.update(() => {
      return {
        ...layout,
      };
    });

    const transformedLayout = this.transformLayout(this._newLayout());
    this._layout.update(() => transformedLayout);
    this.touched.set(true);
  }

  removeComponentFromCell(componentId: number) {}

  updateColumns(value: number) {
    this.touched.set(true);
    const row: CockpitRow | undefined = this.rows().find((row) => row.id === this.selectedRowId());
    if (row != undefined) {
      if (value === row.columns.length) return;

      if (row.columns.length > value) {
        console.log('new row', row);
        row.columns = row.columns.slice(0, value);
        return;
      }

      row.columns.push({ id: crypto.randomUUID(), components: [] });
    }
  }

  updateRowProperties(rowid: string, properties: ElementProperties) {
    const found = this._layout().rows.find((item) => item.id === rowid);
    const newProperties = properties;
    this._layout.update((layout) => {
      return {
        ...layout,
        rows: layout.rows.map((item) =>
          item.id !== rowid ? item : { ...item, properties: newProperties },
        ),
      };
    });
    this.touched.set(true);
  }

  updateVideoConfigProperties(value: videoProperties) {
    this._layout.update((layout) => {
      return {
        ...layout,
        videoConfig: value,
      };
    });
    this.touched.set(true);
  }

  moveItem<T>(array: T[], from: number, to: number): T[] {
    if (from === to) {
      return array;
    }

    if (from < 0 || from >= array.length || to < 0 || to >= array.length) {
      return array;
    }

    const result = [...array];
    const [item] = result.splice(from, 1);
    result.splice(to, 0, item);

    return result;
  }

  // reorders the positions so they are all 0-max number  in the corrrect order
  reorderComponentPositions = (components: IComponents): IComponents => {
    const componentIds = Object.keys(components);
    const newComponents: IComponents = {};

    for (let i = 0; i < componentIds.length; i++) {
      newComponents[componentIds[i]] = {
        ...components[componentIds[i]],
        position: i,
      };
    }

    return newComponents;
  };

  // changes the position of a component
  shuffleComponents = (
    components: IComponents,
    compId: string,
    newPosition: number,
  ): IComponents => {
    const componentIds = Object.keys(components);
    const idsWithoutComponent = componentIds.filter((id) => compId !== id);
    const newIds = [
      ...idsWithoutComponent.slice(0, newPosition),
      compId,
      ...idsWithoutComponent.slice(newPosition, componentIds.length),
    ];
    const newComponents: IComponents = {};

    for (let i = 0; i < componentIds.length; i++) {
      newComponents[newIds[i]] = {
        ...components[newIds[i]],
        position: i,
      };
    }

    console.log('new Components', newComponents);

    return newComponents;
  };

  addComponentToCell = (
    oldComponents: IComponents,
    newComponent: IComponent,
    newPosition: number,
  ) => {
    const componentIds = Object.keys(oldComponents);
    const newIds = [
      componentIds.slice(0, newPosition),
      newComponent.id,
      componentIds.slice(newPosition, componentIds.length),
    ];

    console.log('newIds');
  };

  moveComponentInSameCell(rowId: string, colId: string, componentId: string, newPosition: number) {
    const tempLayout = this._newLayout();
    const row = this._newLayout().rows[rowId];
    const column = row?.columns[colId];
    const components = this._newLayout().rows[rowId].columns[colId].components;
    const newComponents = this.shuffleComponents(components, componentId, newPosition);

    this._newLayout.update((layout) => {
      return {
        ...layout,
        rows: {
          ...layout.rows,
          [rowId]: {
            ...row,
            columns: {
              ...row.columns,
              [colId]: {
                ...column,
                componentsLength: column.componentsLength,
                components: newComponents,
              },
            },
          },
        },
      };
    });

    const transformedLayout = this.transformLayout(this._newLayout());

    this._layout.update(() => transformedLayout);
  }

  filterLayoutFromComponent(rowId: string, colId: string, componentId: string): ILayout {
    const layout = this._newLayout();

    console.log('layout before delete', structuredClone(layout));
    const row: Irow = layout.rows[rowId];

    const components = layout.rows[rowId].columns[colId].components;
    delete components[componentId];
    row.columns[colId].componentsLength = row.columns[colId].componentsLength - 1;

    const reorderedComponents = this.reorderComponentPositions(components);

    layout.rows[rowId].columns[colId].components = reorderedComponents;

    console.log('layout with removed component', layout);
    return layout;
  }

  transferComponentBetweenCells(
    previousRowId: string,
    previousColumnId: string,
    previousPosition: number,
    newRowId: string,
    newColumnId: string,
    newPosition: number,
    componentId: string,
  ) {
    const currentLayout = this._newLayout();
    const previousRow = currentLayout.rows[previousRowId];
    const previousColumn = previousRow.columns[previousColumnId];
    const componentsinPreviousColumn = previousColumn.components;
    const currentRow = currentLayout.rows[newRowId];
    const oldComponent = componentsinPreviousColumn[componentId];

    const layoutWithoutComponent = this.filterLayoutFromComponent(
      previousRowId,
      previousColumnId,
      componentId,
    );
    layoutWithoutComponent.rows[newRowId].columns[newColumnId].components[componentId] =
      oldComponent;
    layoutWithoutComponent.rows[newRowId].columns[newColumnId].componentsLength =
      layoutWithoutComponent.rows[newRowId].columns[newColumnId].componentsLength + 1;
    const components = layoutWithoutComponent.rows[newRowId].columns[newColumnId].components;
    const reorderedComponents = this.reorderComponentPositions(components);
    layoutWithoutComponent.rows[newRowId].columns[newColumnId].components = reorderedComponents;

    const transformedLayout = this.transformLayout(layoutWithoutComponent);
    this._layout.update(() => transformedLayout);
  }

  moveComponent(id: string, rowIndex: number, colId: string) {
    const foundComponent = this._layout()
      .rows[rowIndex].columns.find((col) => col.id === colId)
      ?.components.find((com) => com.id === id);

    this.touched.set(true);
  }

  transformComponents(components: IComponents): GridComponent[] {
    const componentIds = Object.keys(components);
    const gridColumns: GridComponent[] = new Array();

    for (let i = 0; i < componentIds.length; i++) {
      const component = components[componentIds[i]];
      gridColumns[component.position] = {
        id: component.id,
        name: component.name,
         position:1,
        componentSettings: {
          componentId: 'id',
          componentName: 'name1'
        }
      };
    }

    return gridColumns;
  }

  transformColumns(columns: IColumns): GridItem[] {
    const columnIds = Object.keys(columns);
    const columnsArray: GridItem[] = [];

    for (let i = 0; i < columnIds.length; i++) {
      const currentColumn = columns[columnIds[i]];

      columnsArray[currentColumn.position] = {
        id: currentColumn.id,
        components: this.transformComponents(currentColumn.components),
      };
    }
    return columnsArray;
  }

  transformRowstoarray(rows: IRows): CockpitRow[] {
    const rowIds = Object.keys(rows);
    const rowsLength = rowIds.length;
    const rowsArray: CockpitRow[] = new Array(rowsLength);

    for (let i = 0; i < rowsLength; i++) {
      const oldRow = rows[rowIds[i]];
      const newRow: CockpitRow = {
        id: oldRow.id,
        height: oldRow.height,
        columns: this.transformColumns(oldRow.columns),
        properties: oldRow.properties,
      };

      rowsArray[oldRow.position] = newRow;
    }

    return rowsArray;
  }

  setAssignedDeviceIds = (devices: string[]) => {
    this.assignedDeviceIDs.set(devices);

    const transformedLayout = this.transformLayout(this._newLayout());
    this._layout.update(() => transformedLayout);
    this.touched.set(true);
  };

  transformLayout(layout: ILayout): CockpitLayout {
    const oldLayout = layout;
    const newLayout: CockpitLayout = {
      videoConfig: oldLayout.videoConfig,
      rows: this.transformRowstoarray(layout.rows),
    };

    return newLayout;
  }

  saveLayout() {
    const body = {
      assignedDevices: this.assignedDeviceIDs() ?? [],
      layout: this._newLayout(),
    };

    this.http
      .post<{
        layout: {
          data: { dataJSON: ILayout };
        };
        assignedDevices: string[];
      }>('/api/devices/layout', body)
      .pipe(
        tap((response) => {
          console.log('Layout saved:', response);
          const res = response.layout.data.dataJSON;
          this._newLayout.set(res);
          const transformedLayout = this.transformLayout(this._newLayout());
          this.assignedDeviceIDs.set(response.assignedDevices);
          console.log('assigned devices', this.assignedDeviceIDs());
          this._layout.set(transformedLayout);
        }),
        catchError((error) => {
          console.error('Failed to save layout:', error);

          // Return an observable if you want the stream to continue
          return of(null);
        }),
      )
      .subscribe();
  }
}
